import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApps } from '../pages/account-management/apps/hooks/use-apps';
import { useToast } from '@shadcn/ui/hooks/use-toast';

export const OAuthRedirectHandler = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { accounts, isLoading } = useApps();
    const hasProcessedRef = useRef(false);
    const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);

    useEffect(() => {
        const handleOAuthRedirect = async () => {
            // Prevent processing the same OAuth redirect multiple times
            if (hasProcessedRef.current) return;

            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');
            const error = params.get('error');
            const errorDescription = params.get('error_description');

            // Only run if this looks like an OAuth redirect
            if (code || error) {
                console.log('OAuth redirect detected', { code: !!code, error, state });
                setIsProcessingOAuth(true);

                if (error) {
                    console.error('OAuth error:', error, errorDescription);
                    hasProcessedRef.current = true;

                    toast({
                        title: 'Authentication Error',
                        description: `OAuth authentication failed: ${error}${
                            errorDescription ? ` - ${errorDescription}` : ''
                        }`,
                        variant: 'destructive',
                    });

                    // Clean up the URL (remove query params) if this is the main window
                    navigate('/', { replace: true });
                    // Try to close the popup window if this is running in a popup
                    console.log('Closing OAuth popup window');
                    window.close();
                    return;
                }

                if (code && state) {
                    // Wait for useApps to finish loading before calling OAuth handlers
                    if (isLoading) {
                        console.log('Oauth: Waiting for useApps to finish loading...');
                        return; // Don't process yet, wait for next render when isLoading is false
                    }

                    console.log('Oauth: useApps loaded, processing OAuth...');
                    hasProcessedRef.current = true;

                    // Handle Microsoft/Outlook OAuth (state=ms)
                    try {
                        if (state === 'ms') {
                            await accounts.outlook.handleOutlookAuth(code);
                        } else if (state === 'google') {
                            // @NOTE: Does not need to be handled here, since it's handled in the gmail-auth-card.tsx
                            // accounts.gmail.handleGmailAuth(code);
                        } else {
                            console.warn('Oauth: Unknown OAuth state:', state);
                            toast({
                                title: 'Unknown OAuth Provider',
                                description: 'Received OAuth callback from unknown provider.',
                                variant: 'destructive',
                            });
                        }
                    } catch (error) {
                        console.error('Oauth: Error handling OAuth:', error);
                        toast({
                            title: 'OAuth Error',
                            description: 'An error occurred while processing your OAuth request.',
                            variant: 'destructive',
                        });
                    }

                    // Clean up the URL (remove query params) if this is the main window
                    navigate('/', { replace: true });
                    // Try to close the popup window if this is running in a popup
                    console.log('Closing OAuth popup window');
                    window.close();
                }
            }
        };

        handleOAuthRedirect();
    }, [
        location.search,
        navigate,
        accounts.outlook.handleOutlookAuth,
        accounts.gmail.handleGmailAuth,
        toast,
        isLoading,
    ]);

    // Block rendering of children if we're processing OAuth
    if (isProcessingOAuth) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-600">Connecting your account...</p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};
